import { Navigate, useSearchParams, type RouteObject } from "react-router-dom";
import { RequireAdoPat } from "@/components/ado/require-ado-pat";
import { RequireGroomingVisible } from "@/components/grooming/require-grooming-visible";
import { RequireToolboxCapability } from "@/components/toolbox/require-capability";
import { GroomingIndexPage, GroomingLayout } from "./layout";
import { GroomingBacklogPage } from "./backlog";
import { GroomingProjectPage } from "./project";
import { GroomingSessionPage } from "./session";
import { GroomingAgendaSharePage } from "./agenda-share";
import { GroomingOneItemPage } from "./one-item";
import { GroomingQueuePage } from "./queue";
import { DEFAULT_PROJECT_TAB, type ProjectTab } from "@/lib/grooming/paths";

/**
 * The old Repos page's URL (`/grooming/:project/repos`, before PKG-PM-F):
 * `?tab=areas` opens the Project page's Product areas tab, anything else its
 * Repositories tab. Relative, so it stays in the project; the Project route
 * applies the `map-project` gate.
 */
function ReposRedirect() {
  const [searchParams] = useSearchParams();
  const tab: ProjectTab = searchParams.get("tab") === "areas" ? "product-areas" : "repositories";
  return <Navigate to={`../project/${tab}`} replace />;
}

/**
 * The /grooming route map v2 (epic PI-SPEC-SW-E280, feature PI-SPEC-SW-1399),
 * children of AppLayout. The same pages render in a browser and in SpecTree
 * Toolbox. Every route but the shared agenda sits behind
 * RequireGroomingVisible (a Toolbox role on some project, or Admin; E283);
 * Project also needs `map-project` on the project (RequireToolboxCapability).
 *
 * - `project` and `project/:tab` are the Project page (PKG-PM-F, UX-PM-15):
 *   Meeting schedule, Product areas and Repositories. `project` opens the
 *   Meeting schedule.
 * - `repos` is the old Repos page's URL: it redirects to the Project page's
 *   Repositories tab, or its Product areas tab for `?tab=areas`.
 * - `items/:id` is the backlog with the item's reading pane (a deep link).
 * - `session` and `session/:batchId` are the Session screen.
 * - `agenda/:batchId` is the shared agenda: sign-in only, no Toolbox role,
 *   no ADO PAT (PKG-PM-C, epic PI-SPEC-SW-E291, 01-roles.md section 9.6:
 *   meeting attendees hold no role). It reads only the size-free agenda, so
 *   it sits outside RequireGroomingVisible and the PAT-guarded project
 *   layout; AppLayout and the API enforce sign-in.
 * - `grooming/queue` is the run queue (epic PI-SPEC-SW-E284): one per
 *   computer across projects, so project-less like `grooming/one`. It reads
 *   only the app's bridge (no ADO call), so it needs no ADO PAT.
 */
export const groomingRoutes: RouteObject[] = [
  {
    path: "grooming",
    element: (
      <RequireGroomingVisible>
        <RequireAdoPat>
          <GroomingIndexPage />
        </RequireAdoPat>
      </RequireGroomingVisible>
    ),
  },
  {
    path: "grooming/one",
    element: (
      <RequireGroomingVisible>
        <RequireAdoPat>
          <div className="w-full px-4 py-5">
            <GroomingOneItemPage />
          </div>
        </RequireAdoPat>
      </RequireGroomingVisible>
    ),
  },
  {
    path: "grooming/queue",
    element: (
      <RequireGroomingVisible>
        <div className="w-full px-4 py-5">
          <GroomingQueuePage />
        </div>
      </RequireGroomingVisible>
    ),
  },
  {
    path: "grooming/:project/agenda/:batchId",
    element: <GroomingAgendaSharePage />,
  },
  {
    path: "grooming/:project",
    element: (
      <RequireGroomingVisible>
        <RequireAdoPat>
          <GroomingLayout />
        </RequireAdoPat>
      </RequireGroomingVisible>
    ),
    children: [
      { index: true, element: <Navigate to="backlog" replace /> },
      { path: "backlog", element: <GroomingBacklogPage /> },
      { path: "items/:id", element: <GroomingBacklogPage /> },
      { path: "project", element: <Navigate to={DEFAULT_PROJECT_TAB} replace /> },
      {
        path: "project/:tab",
        element: (
          <RequireToolboxCapability capability="map-project">
            <GroomingProjectPage />
          </RequireToolboxCapability>
        ),
      },
      { path: "repos", element: <ReposRedirect /> },
      { path: "session", element: <GroomingSessionPage /> },
      { path: "session/:batchId", element: <GroomingSessionPage /> },
      { path: "one", element: <GroomingOneItemPage /> },
    ],
  },
];
